<template><div><h1 id="projekte" tabindex="-1"><a class="header-anchor" href="#projekte"><span>Projekte</span></a></h1>
<p>Projekte — Definition von Projekten</p>
<h2 id="beschreibung" tabindex="-1"><a class="header-anchor" href="#beschreibung"><span>Beschreibung</span></a></h2>
<p>Jeder Betrag, der im Rahmen einer Spendenakquisition einbezahlt wird, kann auf ein bestimmtes, Projekt verbucht werden. Damit wird ein kampagnen- bzw. projektgestütztes Spendenmarketing möglich. In den Projekten werden alle Beträge für eine bestimmte Zweckbindung zusammengefasst. Dies kann z.B. eine Sammlung für die Finanzierung einer Schule für die Entwicklungshilfe oder auch eine Nothilfe in einem Krisengebiet sein. Im Projekt werden die Sammelziele (pro Periode) definiert und der Stand der Spendeneingänge ist jederzeit online sichtbar.</p>
<p>Bei Spenden von Grossgönnern können neben den Ereignissen auch <RouteLink to="/referenz/refs/ft.refdata.aktionen.html" title="Aktionen">&quot;Aktionen</RouteLink> erfasst. Diese bilden die Grundlage für die Abbildung und Berechnung der zum Projekt ausgewiesenen Beträge.</p>
<p>Diese Dokumentation beschreibt die Standard-Variante der Projekte. Bei einzelnen Organisationen werden kundenspezifische Versionen eingesetzt, die von dieser Dokumentation abweichen.</p>
<p>Pfad: <code v-pre>Projektfinanzierung → Projekte</code></p>
<h2 id="datenfelder" tabindex="-1"><a class="header-anchor" href="#datenfelder"><span>Datenfelder</span></a></h2>
<dl>
<dt>Projekt/Projekttitel</dt>
<dd>Name des Projektes (editierbar).</dd>
<dt>Kurzbezeichnung</dt>
<dd>Projektbezeichnung in einer kurzen Variante. Wird z. T. auch für einen codierten Projektnamen verwendet.</dd>
<dt>Beschreibung</dt>
<dd>Ausführlichere Projektbeschreibung</dd>
<dt>Status</dt>
<dd>
<table>
<thead>
<tr>
<th>Status</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>aktiv</td>
<td>aktives Projekt ohne detaillierten Fundraising-Status</td>
</tr>
<tr>
<td>bewilligt</td>
<td>Start Fundraising bewilligt</td>
</tr>
<tr>
<td>finanziert</td>
<td>Projektziel ist erreicht worden</td>
</tr>
<tr>
<td>anulliert</td>
<td>Fundraising abgebrochen/wird nicht gestartet</td>
</tr>
<tr>
<td>abgeschlossen</td>
<td>Fundraisingaktivitäten abgeschlossen</td>
</tr>
<tr>
<td>unterbrochen</td>
<td>Fundraisingaktivitäten unterbrochen</td>
</tr>
</tbody>
</table>
</dd>
<dt>Priorität</dt>
<dd>Klassierung nach Prioritäten &quot;tief&quot;, &quot;normal&quot;, &quot;hoch&quot;</dd>
<dt>Projekttyp/ft-Projekttyp</dt>
<dd>Verknüpfung des Projektes mit dem <RouteLink to="/referenz/refs/ft.refdata.tblprojekttypen.html" title="Projekttypen">Projekttyp</RouteLink>.</dd>
<dt>Laufzeit von/bis</dt>
<dd>Dauer des Projektes</dd>
<dt>betreut durch</dt>
<dd>Interner Mitarbeiter, der das Projekt betreut. Lookup auf funtrade-User</dd>
<dt>Folgeprojekt von</dt>
<dd>Eintrag Vorgängerprojekt (Lookup auf Projekte).</dd>
<dt>Bemerkung</dt>
<dd>Textfeld für Bemerkungen</dd>
<dt>Gruppe &quot;Projektziel&quot;:</dt>
<dd>Gesamt-Projektziel, basierend auf den Jahreszielen, abzüglich der vorbestehenden Erträge.</dd>
<dt>Projektziel</dt>
<dd>Summe der einzelnen Projketziele pro Periode, die unter <RouteLink to="/referenz/refs/ft.refdata.umsatzprojahr.html" title="Umsatz pro Jahr">Umsatz pro Jahr</RouteLink> eingetragen werden.</dd>
<dt>Vorbesteheneder Ertrag</dt>
<dd>Bereits realisierter Ertrag, der nicht direkt aus diesem Projekt stammt (wird vom Projektziel abgezogen).</dd>
<dt>Projektziel Netto</dt>
<dd>Projektziel Netto = [Projektziel] - [Vorbestehender Ertrag]</dd>
<dt>Gruppe &quot;Einnahmen:</dt>
<dd>Gesamttotal aller Einnahmen, summiert aus allen Perioden</dd>
<dt>Kollektivmarketing</dt>
<dd>Eingang von Zahlungen auf das Projekt, welche über ein Kollektivmarketing-Ereignis akquiriert wurden. Entscheidend für die Verbuchung der Zahlungen unter Kollektivmarketing ist der Schalter in der Ereignisdefinition, sowie die Verknüpfung des Ereignisses mit dem Projekt. Details siehe <RouteLink to="/referenz/refs/ft.refdata.ereigknuepfman.html" title="Ereignisse zu Person">Manuelle Ereignisverknüpfung</RouteLink>.</dd>
<dt>Individualmarketing</dt>
<dd>Eingang von Zahlungen auf das Projekt, welche über ein Individualmarketing-Ereignis akquiriert wurden. Entscheidend für die Verbuchung der Zahlungen unter Individualmarketing ist der Schalter in der Ereignisdefinition sowie die Verknüpfung des Ereignisses mit dem Projekt.</dd>
<dt>Weitere Quellen</dt>
<dd>Summe der Beträge gemäss Tabelle <RouteLink to="/referenz/refs/ft.refdata.weiterequellen.html" title="Weitere Quellen">Weitere Quellen</RouteLink> zu Projekt und Jahr (manuell erfasst oder aus <RouteLink to="/referenz/refs/ft.refdata.aktionscodes.html" title="Aktionen Stammdaten">Aktionen</RouteLink> mit Zuordnung &quot;weitere Quellen&quot;).</dd>
<dt>Einnahmen</dt>
<dd>Einnahmen Total = [Summe Kollektivmarketing] + [Summe Individualmarketing] + [Summe weitere Quellen]</dd>
<dt>Gruppe &quot;Aktionen&quot;:</dt>
<dd>Beträge, berechnet aus den mit dem Projekt verknüpften Aktionen. In der <RouteLink to="/referenz/refs/ft.refdata.aktionscodes.html" title="Aktionen Stammdaten">Aktionen-Stammtabelle</RouteLink> wird pro Aktionstyp definiert, ob der eingetragenen Betrag im Projekt-Kopf angezeigt werden soll oder nicht.</dd>
<dt>Gruppe &quot;Projektstand&quot;:</dt>
<dd>Übersicht Projektstand/Zielerreichung.</dd>
<dt>Projektziel</dt>
<dd>Projektziel (netto). Gleiches Feld wie in der Gruppe &quot;Projektziel&quot;.</dd>
<dt>Einnahmen</dt>
<dd>Einnahmen total. Gleiches Feld wie in der Gruppe &quot;Einnahmen&quot;.</dd>
<dt>Prognose (Einnahmen)</dt>
<dd>Einnahmen berechnet mit Prognosebetrag aus den Aktionen.</dd>
<dt>= Differenz zum Ziel</dt>
<dd>Differenz zum Ziel = [Einnahmen] - [Projektziel] -</dd>
<dt>Kosten</dt>
<dd>Kosten, summiert aus den <RouteLink to="/manual/customer/ereignisse-und-ereignispositionen.html#ereigniskosten" title="4.10. Ereigniskosten">Ereigniskosten</RouteLink> der mit dem Projekt verknüpften Ereignisse.</dd>
<dt>Ertrag</dt>
<dd>Ertrag = [Einnahmen] - [Kosten].</dd>
<dt>Kosten</dt>
<dd>Summe der Ereigniskosten</dd>
<dt>Diff. zum Ziel netto</dt>
<dd>Diff. zum Ziel netto = [Diff. zum Ziel] - [Ausgaben Total]</dd>
<dt>ROI</dt>
<dd>ROI (Return on Investment) berechnet sich wie folgt: ROI = Gewinnanteil / Kapitaleinsatz<br>in funtrade, bezogen auf Ereignisse, resp. Ereignisebenen: ROI = [Ertrag] / [Kosten]</dd>
</dl>
<h2 id="funktionen" tabindex="-1"><a class="header-anchor" href="#funktionen"><span>Funktionen</span></a></h2>
<p>N/A</p>
<h2 id="siehe-auch" tabindex="-1"><a class="header-anchor" href="#siehe-auch"><span>Siehe auch</span></a></h2>
<p><RouteLink to="/referenz/refs/ft.refdata.aktionscodes.html" title="Aktionen Stammdaten">Aktionen</RouteLink> , <RouteLink to="/manual/customer/ereignisse-und-ereignispositionen.html#ereigniskosten" title="4.10. Ereigniskosten">Ereigniskosten</RouteLink> , <RouteLink to="/referenz/refs/ft.refdata.umsatzprojahr.html" title="Umsatz pro Jahr">Projektzahlen pro Periode</RouteLink> , <RouteLink to="/referenz/refs/ft.refdata.weiterequellen.html" title="Weitere Quellen">Weitere Quellen zu Projekt und Jahr</RouteLink></p>
</div></template>


